import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trainersAPI, trainingRequestsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Mail,
  Phone,
  Globe,
  Star,
  CalendarDays,
  Award,
  Tag,
  Users,
  Video,
  MapPin,
  Bookmark,
  TrendingUp,
  GraduationCap,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
  ScatterChart,
} from 'recharts';
import { toast } from 'sonner';

const SESSION_TYPE_ICON = {
  virtual: <Video className="w-3.5 h-3.5 text-blue-500" />,
  in_person: <MapPin className="w-3.5 h-3.5 text-green-500" />,
  hybrid: <Users className="w-3.5 h-3.5 text-purple-500" />,
};

const SESSION_TYPE_LABEL = { virtual: 'Virtual', in_person: 'In-Person', hybrid: 'Hybrid' };

const StarDisplay = ({ value }) => {
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <div className="flex items-center gap-1">
      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
      <span className="font-medium text-sm">{value.toFixed(1)}</span>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border rounded-lg p-2.5 shadow-md text-sm">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.value?.toFixed(1)}</p>
      ))}
    </div>
  );
};

export const TrainerProfilePage = () => {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const [trainer, setTrainer] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [trainerData, sessionsData, projectsData] = await Promise.all([
        trainersAPI.get(trainerId),
        trainersAPI.getSessions(trainerId),
        trainingRequestsAPI.getAll({ limit: 10000 }).catch(() => []),
      ]);
      setTrainer(trainerData);
      setSessions(sessionsData);
      setProjects(projectsData);
    } catch (err) {
      toast.error('Failed to load trainer profile');
    } finally {
      setLoading(false);
    }
  }, [trainerId]);

  useEffect(() => { load(); }, [load]);

  const getProjectName = (pid) => projects.find(p => p.id === pid)?.title || '—';

  // Build ratings timeline from sessions with feedback
  const ratingsData = sessions
    .filter(s => s.feedback?.mtm_trainer_rating && s.session_date)
    .map(s => ({
      date: s.session_date,
      rating: s.feedback.mtm_trainer_rating,
      label: s.session_date
        ? format(parseISO(s.session_date), 'MMM yy')
        : '—',
      title: s.title || s.course_title || 'Session',
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-48 bg-muted animate-pulse rounded-xl" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Trainer not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/trainers')}>
          Back to Trainers
        </Button>
      </div>
    );
  }

  const completedSessions = sessions.filter(s => s.status === 'completed').length;
  const totalLearners = sessions.reduce((n, s) => n + (s.attended_count || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trainer-profile-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/trainers')} className="-mb-2 -ml-2 text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4 mr-2" />
        All Trainers
      </Button>

      {/* Profile Header */}
      <div className="rounded-xl overflow-hidden border bg-card shadow-sm">
        <div className="h-20 bg-gradient-to-r from-violet-600 via-blue-600 to-indigo-700" />
        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 -mt-10">
            {/* Avatar */}
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-background border-4 border-background shadow-md flex items-center justify-center text-2xl font-bold bg-gradient-to-br from-violet-500 to-blue-600 text-white">
                {trainer.name?.charAt(0).toUpperCase()}
              </div>
              {trainer.is_featured && (
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center shadow">
                  <Bookmark className="w-3.5 h-3.5 text-white fill-white" />
                </div>
              )}
            </div>
            {/* Name + meta */}
            <div className="flex-1 pb-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-heading font-bold">{trainer.name}</h1>
                {trainer.is_featured && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300">Featured</Badge>
                )}
                {!trainer.is_active && (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-4 mt-1.5 text-sm text-muted-foreground">
                {trainer.email && (
                  <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{trainer.email}</span>
                )}
                {trainer.phone && (
                  <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{trainer.phone}</span>
                )}
                {trainer.country && (
                  <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" />{trainer.country}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                <CalendarDays className="w-4.5 h-4.5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Sessions</p>
                <p className="text-xl font-bold font-heading">{trainer.total_sessions || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <Star className="w-4.5 h-4.5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Rating</p>
                <p className="text-xl font-bold font-heading">
                  {trainer.avg_rating != null ? `${trainer.avg_rating}/5` : '—'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-lime/20 flex items-center justify-center">
                <GraduationCap className="w-4.5 h-4.5 text-lime-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Learners Trained</p>
                <p className="text-xl font-bold font-heading">{totalLearners}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-4.5 h-4.5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Completed Sessions</p>
                <p className="text-xl font-bold font-heading">{completedSessions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="sessions">
        <TabsList>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Session History</TabsTrigger>
          <TabsTrigger value="ratings" data-testid="tab-ratings">Ratings Timeline</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
        </TabsList>

        {/* Session History Tab */}
        <TabsContent value="sessions" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Session History</CardTitle>
              <CardDescription className="text-xs">{sessions.length} session{sessions.length !== 1 ? 's' : ''} found</CardDescription>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No sessions found for this trainer</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Session / Course</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Attendance</TableHead>
                      <TableHead>Rating</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map(s => (
                      <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                        <TableCell className="text-sm whitespace-nowrap">
                          {s.session_date
                            ? format(parseISO(s.session_date), 'MMM d, yyyy')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm leading-tight">{s.title || s.course_title || '—'}</p>
                            {s.course_code && <p className="text-xs text-muted-foreground">{s.course_code}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[160px] truncate">
                          {getProjectName(s.training_project_id)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-sm">
                            {SESSION_TYPE_ICON[s.session_type]}
                            <span className="text-xs">{SESSION_TYPE_LABEL[s.session_type] || s.session_type}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              s.status === 'completed' ? 'border-green-300 text-green-700 bg-green-50' :
                              s.status === 'scheduled' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                              s.status === 'cancelled' ? 'border-red-300 text-red-700 bg-red-50' :
                              ''
                            }
                          >
                            {s.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {s.attended_count != null
                            ? `${s.attended_count}/${s.expected_count || '?'}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <StarDisplay value={s.feedback?.mtm_trainer_rating} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ratings Timeline Tab */}
        <TabsContent value="ratings" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Ratings Timeline</CardTitle>
              <CardDescription className="text-xs">MTM Trainer Rating per session over time</CardDescription>
            </CardHeader>
            <CardContent>
              {ratingsData.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Star className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No feedback ratings available yet</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={ratingsData} margin={{ top: 5, right: 20, left: -15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                    <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} tickLine={false} />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <ReferenceLine y={trainer.avg_rating} stroke="#C8E600" strokeDasharray="4 4" label={{ value: `Avg ${trainer.avg_rating}`, fontSize: 11, fill: '#C8E600' }} />
                    <Line
                      type="monotone"
                      dataKey="rating"
                      name="Rating"
                      stroke="#8B5CF6"
                      strokeWidth={2.5}
                      dot={{ r: 5, fill: '#8B5CF6', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Bio */}
            {trainer.bio && (
              <Card className="md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">About</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed">{trainer.bio}</p>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <Award className="w-4 h-4" />Certifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trainer.certifications?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {trainer.certifications.map(c => (
                      <Badge key={c} variant="outline" className="font-mono text-xs">{c}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No certifications listed</p>
                )}
              </CardContent>
            </Card>

            {/* Expertise Tags */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <Tag className="w-4 h-4" />Expertise
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trainer.expertise_tags?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {trainer.expertise_tags.map(t => (
                      <Badge key={t} className="bg-violet-100 text-violet-800 text-xs border-violet-200">{t}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No expertise tags listed</p>
                )}
              </CardContent>
            </Card>

            {/* Solution Areas */}
            {trainer.solution_areas?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Solution Areas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {trainer.solution_areas.map(s => (
                      <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {trainer.country && (
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span>{trainer.country}</span>
                  </div>
                )}
                {trainer.timezone && (
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span>{trainer.timezone}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TrainerProfilePage;
